import { Input, notification } from 'antd';
import { Button } from 'components/common';
import { ShareSelect } from 'components/common/ShareSelect';
import { useAuth } from 'hooks';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  exportTemplateActions,
  exportTemplateSelectors as selectors,
} from 'reducers/exportTemplate/reducer';
import { useCreateTemplateMutation } from '../hooks/useCreateTemplateMutation';
import { useUpdateTemplateMutation } from '../hooks/useUpdateTemplateMutation';
import {
  Label,
  ModalContentSection,
  ModalFooter,
  SummaryBox,
  SummaryLabel,
  SummaryValue,
  H2,
  Paragraph,
} from '../styled';
import { save as strings } from '../strings';
import { ExportTemplateModalHeader } from './ExportTemplateModalHeader';

const MAX_NAME_LENGTH = 50;
const charCountStyle = {
  position: 'absolute',
  right: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  fontSize: 12,
  color: '#999',
};

/**
 * Final step for saving a template - enter name, share settings, and view summary.
 */
const SaveTemplate = forwardRef(function SaveTemplate(
  { onCancel, onBack },
  ref
) {
  const dispatch = useDispatch();
  const { user } = useAuth();

  const isSettingsView = useSelector(selectors.isInSettingsView);
  const isNewTemplate = useSelector(selectors.isNewTemplate);

  const sheetsPayload = useSelector(selectors.getEditingSheetsPayload);
  const enabledSheetsCount = useSelector(selectors.countEditingEnabledSheets);
  const enabledColumnsCount = useSelector(selectors.countEditingEnabledColumns);
  const renamedColumnsCount = useSelector(selectors.countEditingRenamedItems);
  const metadataSheetsCount = useSelector(selectors.countMetadataSheets);
  const metadataColumnsCount = useSelector(selectors.countEditingTotalColumns);

  const sourceTemplateName = useSelector(selectors.getExistingTemplateName);
  const sourceTemplateId = useSelector(selectors.getExistingTemplateId);
  const sourceTemplateSharedWithIds = useSelector(
    selectors.getExistingTemplateSharedWithIds
  );

  const canCurrentUserShare = user.isUserManager;

  // Don't pre-fill "All fields" as that's just a display name for the modal title
  const [name, setName] = useState('');
  const [sharedWith, setSharedWith] = useState([]);

  const initialValuesRef = useRef({ name: '', sharedWith: [] });

  useEffect(() => {
    if (isSettingsView) {
      const templateName = sourceTemplateName || '';
      const templateSharedWith = sourceTemplateSharedWithIds || [];
      setName(templateName);
      setSharedWith(templateSharedWith);
      initialValuesRef.current = {
        name: templateName,
        sharedWith: templateSharedWith,
      };
    }
  }, [isSettingsView, sourceTemplateName, sourceTemplateSharedWithIds]);

  const saveButtonLabel = isSettingsView
    ? strings.confirm
    : isNewTemplate
    ? strings.createTemplate
    : strings.saveTemplate;

  const handleMutationSuccess = useCallback(
    res => {
      if (res?.id != null && isNewTemplate) {
        dispatch(
          exportTemplateActions.dropdownTemplateAdded({
            id: res.id,
            name: name.trim(),
            fieldCount: enabledColumnsCount,
            availableFieldCount: enabledColumnsCount,
          })
        );
      }
      dispatch(exportTemplateActions.serverMutationOccurred());
      dispatch(exportTemplateActions.editModalClosed());
    },
    [dispatch, isNewTemplate, name, enabledColumnsCount]
  );

  const { triggerCreate, createLoading } = useCreateTemplateMutation(
    handleMutationSuccess
  );

  const { triggerUpdate, updateLoading } = useUpdateTemplateMutation(
    handleMutationSuccess
  );

  const saving = createLoading || updateLoading;

  const isValidName = name.trim().length > 0;

  const hasUnsavedChanges = (() => {
    const initial = initialValuesRef.current;
    if (name !== initial.name) return true;
    if (sharedWith.length !== initial.sharedWith.length) return true;
    const initialSet = new Set(initial.sharedWith);
    return sharedWith.some(id => !initialSet.has(id));
  })();

  useImperativeHandle(ref, () => ({ isFormDirty: () => hasUnsavedChanges }));

  const handleSave = () => {
    if (isSettingsView) {
      triggerUpdate(sourceTemplateId, {
        name: name.trim(),
        sharedWithUserIds: sharedWith.length > 0 ? sharedWith : [],
      });
    } else {
      if (
        sheetsPayload.length === 0 ||
        !sheetsPayload.some(s => s.columns.length > 0)
      ) {
        notification.warning({
          message: strings.warningNoColumns,
          placement: 'bottomLeft',
          duration: 6,
        });
        return;
      }
      triggerCreate({
        name: name.trim(),
        sheets: sheetsPayload,
        sharedWithUserIds: sharedWith.length > 0 ? sharedWith : undefined,
      });
    }
  };

  const handleNameChange = e => {
    if (e.target.value.length <= MAX_NAME_LENGTH) {
      setName(e.target.value);
    }
  };

  return (
    <div>
      <ExportTemplateModalHeader />
      <ModalContentSection>
        <Label>
          {strings.templateName}
          <span style={{ color: '#ff4d4f', marginLeft: 2 }}>*</span>
        </Label>
        <div style={{ position: 'relative' }}>
          <Input
            data-testid='et-modal-template-name-input'
            value={name}
            onChange={handleNameChange}
            placeholder={strings.namePlaceholder}
            style={{ paddingRight: 50 }}
          />
          <span style={charCountStyle}>
            {name.length}/{MAX_NAME_LENGTH}
          </span>
        </div>
      </ModalContentSection>

      {canCurrentUserShare && (
        <ModalContentSection>
          <H2>{strings.share}</H2>
          <Paragraph>{strings.shareDescription}</Paragraph>
          <ShareSelect setSharedWith={setSharedWith} sharedWith={sharedWith} />
        </ModalContentSection>
      )}
      {!isSettingsView && (
        <ModalContentSection>
          <H2>Template Summary</H2>
          <div style={{ display: 'flex', gap: 15, marginTop: 15 }}>
            <SummaryBox>
              <SummaryValue>{enabledSheetsCount}</SummaryValue>
              <SummaryLabel>
                {strings.tabsIncluded(enabledSheetsCount, metadataSheetsCount)}
              </SummaryLabel>
            </SummaryBox>
            <SummaryBox>
              <SummaryValue>{enabledColumnsCount}</SummaryValue>
              <SummaryLabel>
                {strings.fieldsIncluded(
                  enabledColumnsCount,
                  metadataColumnsCount
                )}
              </SummaryLabel>
            </SummaryBox>
            <SummaryBox>
              <SummaryValue>{renamedColumnsCount}</SummaryValue>
              <SummaryLabel>{strings.itemsRenamed}</SummaryLabel>
            </SummaryBox>
          </div>
        </ModalContentSection>
      )}

      <ModalFooter>
        {!isSettingsView && <Button onClick={onBack}>{strings.back}</Button>}
        {isSettingsView && <Button onClick={onCancel}>{strings.cancel}</Button>}
        <Button
          data-testid='et-modal-create-template-button'
          type='primary'
          onClick={handleSave}
          loading={saving}
          disabled={!isValidName}
        >
          {saveButtonLabel}
        </Button>
      </ModalFooter>
    </div>
  );
});

export default SaveTemplate;
